/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import { AppEmbed } from "@thoughtspot/visual-embed-sdk/react";
import { EmbedProps } from "@thoughtspot/visual-embed-sdk/lib/src/react/util";
import {
  Action,
  AppViewConfig,
  HomePageSearchBarMode,
  MessagePayload,
  Page,
} from "@thoughtspot/visual-embed-sdk";
import { getUserSessionItem, initTs } from "../..";
import {
  useAppSelector,
  useValidatedUserPayload,
  useAppDispatch,
} from "../../app/hooks";
import {
  selectUserAuthContext,
  updateThoughtSpotAppEmbedContext,
} from "../../features/console/consoleSlice";
import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useLazyGetTSAccessTokenQuery } from "../../services/serverless";
import { smallScreenCssRules } from "./smallScreenCssRules";
import { useResizeDetector } from "react-resize-detector";
import { Error as ErrorComponent } from "../Error";
import { sessionChecker } from "../../helpers/session";
import { SubdomainOrgIdObject } from "../../routing/FunctionsAndInterfaces";
import { useLazyGetPillsByWorkspaceQuery } from "../../services/gallusTools";
import { ButtonAndDescription } from "../TSLandingPage";

export interface AppProps extends EmbedProps, AppViewConfig {
  orgObject: SubdomainOrgIdObject;
  cssRules: {
    [selector: string]: {
      [declaration: string]: string;
    };
  };
  breakPoint?: "xxl" | "xl" | "lg" | "md" | "sm" | "xs";
  defaultPage?: Page;
  enableFullFeatures?: boolean;
  disableEditButtonArray?: string[];
  disableEditButtonDynamicCodeArray?: {
    code: string;
    backup?: ButtonAndDescription[];
  }[];
  disableFunctionality?: { functionality: Action; bypassGroup: string }[];
  disableCompactLiveboardHeader?: boolean;
}

export const AppEmbedWrapper = (props: AppProps) => {
  let defaultDisabledActions: Action[] = [
    Action.ImportTML,
    Action.Schedule,
    Action.SchedulesList,
  ];

  const userSession = useValidatedUserPayload();

  React.useEffect(() => {
    if (userSession.cognitoGroups) {
      let tmpDisabledActionsArray = [...defaultDisabledActions];
      if (userSession.cognitoGroups.includes("ENABLEIMPORTTML")) {
        tmpDisabledActionsArray = defaultDisabledActions.filter(
          (action) => action !== Action.ImportTML,
        );
      }
      if (props.disableFunctionality) {
        props.disableFunctionality.forEach((functionality) => {
          if (
            userSession.cognitoGroups &&
            !userSession.cognitoGroups.includes(functionality.bypassGroup)
          ) {
            tmpDisabledActionsArray.push(functionality.functionality);
          }
        });
      }
      setNormalDisabledActions(tmpDisabledActionsArray);
    }
  }, [userSession]);

  const dispatch = useAppDispatch();
  const userAuthContext = useAppSelector(selectUserAuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [hasNoCookieAccess, setHasNoCookieAccess] =
    React.useState<boolean>(false);
  const [modalIsOpen, setModalIsOpen] = React.useState<boolean>(false);
  const [pinnedLiveboard, setPinnedLiveboard] = React.useState<string>("");
  const [embedPath, setEmbedPath] = React.useState<string>("");
  const [normalDisabledActions, setNormalDisabledActions] = React.useState<
    Action[]
  >([...defaultDisabledActions]);
  const [currentQueryParameters, setSearchParams] = useSearchParams();
  const newQueryParameter: URLSearchParams = new URLSearchParams();
  // const tsAccessTokenResult = useGetTSAccessTokenQuery(props.orgId ?? 0);
  const [getTSAccessToken] = useLazyGetTSAccessTokenQuery();
  const [disabledActionsArray, setDisabledActionsArray] = React.useState<
    Action[]
  >(!props.enableFullFeatures ? normalDisabledActions : []);
  const [getTilesQuery, getTilesQueryResult] =
    useLazyGetPillsByWorkspaceQuery();
  const [disabledLiveboardsArray, setDisabledLiveboardsArray] = React.useState<
    string[]
  >([]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { width, height, ref } = useResizeDetector();
  const [rulesHaveBeenSet, setRulesHaveBeenSet] =
    React.useState<boolean>(false);

  //forceReRender is a state used ONLY to force the appEmbed hook to rerender.
  //NO clue why changing embedPath doesn't trigger a re render and this does.
  const [forceReRender, setForceReRender] = React.useState<boolean>(false);

  const currentDisabledActions = React.useRef<string[]>(
    !props.enableFullFeatures ? normalDisabledActions : [],
  );

  const [currentCssRules, setCurrentCssRules] = React.useState<{
    [selector: string]: {
      [declaration: string]: string;
    };
  }>();

  const onLiveboardRendered = (payload: MessagePayload) => {
    console.log("liveboard rendered");
  };

  const handleNoCookieAccess = () => {
    console.log("no cookie access");
    setHasNoCookieAccess(true);
  };

  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen);
  };

  const redirect = () => {
    newQueryParameter.set("productURL", "pinboard/" + pinnedLiveboard);
    setSearchParams(newQueryParameter);
    setEmbedPath("pinboard/" + pinnedLiveboard);
  };

  React.useEffect(() => {
    let productUrl = currentQueryParameters.get("productURL");
    const fromMenu = currentQueryParameters.get("fromMenu");
    if (productUrl) {
      productUrl = productUrl.replace("/insights/", "");
      setEmbedPath(productUrl);
    }
    if (fromMenu) {
      // setEmbedPath(productUrl.replace("%2F", "/"));
      setForceReRender(!forceReRender);
    }
  }, [currentQueryParameters]);

  const addLiveboardLinksToArray = (
    originalArray: string[],
    newElements: ButtonAndDescription[],
  ) => {
    return [
      ...originalArray,
      ...newElements.map((value) => {
        if (value.link) {
          return value.link.slice(9);
        } else {
          return "pinboard/";
        }
      }),
    ];
  };

  React.useEffect(() => {
    const getNonEditableLiveboards = async () => {
      let dynamicNonEditableLiveboards: string[] = [];
      let staticNonEditableLiveboards: string[] = [];

      if (props.disableEditButtonDynamicCodeArray) {
        for (const dynamicTileCode of props.disableEditButtonDynamicCodeArray) {
          try {
            const liveboards = await getTilesQuery({
              workspace: dynamicTileCode.code,
            }).unwrap();
            if (liveboards.length === 0 && dynamicTileCode.backup) {
              //Redundant check. Some firewalls block the call but it doesn't register it as a failed call.
              dynamicNonEditableLiveboards = addLiveboardLinksToArray(
                dynamicNonEditableLiveboards,
                dynamicTileCode.backup,
              );
            } else {
              dynamicNonEditableLiveboards = [
                ...dynamicNonEditableLiveboards,
                ...liveboards.map((value) => {
                  return value.link.slice(9);
                }),
              ];
            }
          } catch {
            if (dynamicTileCode.backup) {
              dynamicNonEditableLiveboards = addLiveboardLinksToArray(
                dynamicNonEditableLiveboards,
                dynamicTileCode.backup,
              );
            }
          }
        }
      }

      if (props.disableEditButtonArray) {
        staticNonEditableLiveboards = [...props.disableEditButtonArray];
      }

      setDisabledLiveboardsArray([
        ...dynamicNonEditableLiveboards,
        ...staticNonEditableLiveboards,
      ]);
    };
    getNonEditableLiveboards();
  }, []);

  React.useEffect(() => {
    handleEditingAccess(embedPath);
  }, [disabledActionsArray]);

  const handleEditingAccess = (currentPath: string) => {
    if (
      userSession.cognitoGroups &&
      (props.disableEditButtonArray ||
        props.disableEditButtonDynamicCodeArray) &&
      props.orgObject.orgID &&
      props.orgObject.editableRightsGroup
    ) {
      let userCanEdit = userSession.cognitoGroups?.includes(
        props.orgObject.editableRightsGroup,
      );

      //if the user does NOT belong to the correct group...
      if (!userCanEdit) {
        //disable liveboards editing button
        let disableEditButtonRequired = false;
        disableEditButtonRequired =
          disabledLiveboardsArray.some((liveboardId) => {
            return currentPath.includes(liveboardId);
          }) ?? false;

        let newDisabledActions = [];
        if (disableEditButtonRequired) {
          newDisabledActions = !props.enableFullFeatures
            ? [...normalDisabledActions, Action.Edit, Action.Remove]
            : [Action.Edit, Action.Remove];
        } else {
          newDisabledActions = !props.enableFullFeatures
            ? normalDisabledActions
            : [];
        }

        if (
          newDisabledActions.length !== currentDisabledActions.current.length &&
          currentPath.includes("pinboard/")
        ) {
          setDisabledActionsArray(newDisabledActions);
          currentDisabledActions.current = newDisabledActions;
        }
      } else {
        if (
          currentCssRules &&
          currentCssRules['.classic-nav-module__navItem[href="/#/data/tables"]']
        ) {
          var {
            '.classic-nav-module__navItem[href="/#/data/tables"]': _,
            ...newCssRules
          } = currentCssRules;
          setCurrentCssRules(newCssRules);
        }
      }
    }
  };

  const handleRouteChanged = (payload: MessagePayload) => {
    //the search parameters change according to the current route, so users can share links easily
    //I remove the first character ("/"), to be a little cleaner
    newQueryParameter.set("productURL", payload.data.currentPath);
    setSearchParams(newQueryParameter);

    handleEditingAccess(payload.data.currentPath);
  };

  handleEditingAccess(embedPath); //in case user reloads a page that should not be editable

  React.useEffect(() => {
    if (width && !rulesHaveBeenSet) {
      setRulesHaveBeenSet(true);
      setCurrentCssRules(cssRulesHelper(width > 650)); //Screen starts looking bad at around 650px width
      console.log(
        "Setting CSS rules: " + (width > 650 ? "Full Screen" : "Mobile"),
      );
    }
  }, [width]);

  const cssRulesHelper: any = (normalRules: boolean) => {
    if (normalRules) {
      return props.cssRules;
    } else {
      return { ...props.cssRules, ...smallScreenCssRules };
    }
  };

  async function handleAuthExpire(payload: MessagePayload) {
    console.error(payload);
    // Make sure the Cognito access token is not expired so that we could use it to generate the TS access token. Otherwise, it redirects the user to the login page.
    await sessionChecker(() => {
      navigate(
        `/login?redirect_uri=${encodeURIComponent(`${location.pathname}${location.search}`)}`,
      );
    });
  }

  async function handleAuthFailure(payload: MessagePayload) {
    console.error(payload);
    await sessionChecker(() => {
      navigate(
        `/login?redirect_uri=${encodeURIComponent(`${location.pathname}${location.search}`)}`,
      );
    });
  }

  function handleAuthInit(payload: MessagePayload) {
    console.debug(payload);
  }

  function handleAll(payload: MessagePayload) {
    dispatch(
      updateThoughtSpotAppEmbedContext({
        data: payload,
      }),
    );
  }

  const [appEmbed, setAppEmbed] = React.useState<JSX.Element>();
  React.useEffect(() => {
    const render = async () => {
      //A new variable is used to point the embed to the current path. Using the embedPath
      //state causes the embed to default to the liveboards page, on an apparent internal
      //race condition on rerender.
      let currentPath = "home/liveboards";
      if (currentQueryParameters.get("productURL") !== null) {
        currentPath = currentQueryParameters
          .get("productURL")!
          .toString()
          .replace("%2F", "/");
      } else {
        currentPath = embedPath;
      }

      try {
        await sessionChecker(() => {
          navigate(
            `/login?redirect_uri=${encodeURIComponent(`${location.pathname}${location.search}`)}`,
          );
        });
        await getTSAccessToken(props.orgObject.orgID ?? 0).unwrap();
        await initTs(
          "TOKEN",
          userAuthContext?.userName ?? getUserSessionItem("userName"),
          props.orgObject.orgID ?? 0,
        );
        setAppEmbed(
          <div ref={ref} style={{ height: "100%" }}>
            <AppEmbed
              isUnifiedSearchExperienceEnabled={false}
              homePageSearchBarMode={HomePageSearchBarMode.AI_ANSWER}
              onALL={handleAll}
              onAuthExpire={handleAuthExpire}
              onAuthFailure={handleAuthFailure}
              onAuthInit={handleAuthInit}
              dataPanelV2={true}
              disabledActions={disabledActionsArray}
              hiddenActions={disabledActionsArray}
              isLiveboardCompactHeaderEnabled={
                props.disableCompactLiveboardHeader ? false : true
              }
              customizations={{
                style: {
                  customCSS: {
                    rules_UNSTABLE: currentCssRules ?? props.cssRules,
                  },
                },
              }}
              {...props}
              onLiveboardRendered={onLiveboardRendered}
              pageId={props.defaultPage ?? Page.Liveboards}
              path={currentPath}
              onNoCookieAccess={handleNoCookieAccess}
              /* onPin={(payload) => {
                setPinnedLiveboard(payload.data.liveboardId);
                setModalIsOpen(true);
              }} */
              onRouteChange={handleRouteChanged}
            />
          </div>,
        );
      } catch (e) {
        setAppEmbed(
          <ErrorComponent type="External" code="500" message="" error={e} />,
        );
      }
    };
    render();
  }, [
    hasNoCookieAccess,
    props,
    forceReRender,
    disabledActionsArray,
    currentCssRules,
  ]);

  return (
    <>
      {hasNoCookieAccess && (
        <Alert color="info">
          Please allow third party cookies from the browser settings.
        </Alert>
      )}
      {appEmbed}
      <Modal isOpen={modalIsOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Pinned Board</ModalHeader>
        <div>
          <ModalBody>Do you want to go to the liveboard?</ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={() => {
                toggleModal();
                redirect();
              }}
            >
              Yes
            </Button>
            <Button color="secondary" onClick={() => toggleModal()}>
              Cancel
            </Button>
          </ModalFooter>
        </div>
      </Modal>
    </>
  );
};
